import { useQuery } from "@apollo/client/react";
import { Button, Chip, CircularProgress, Stack, Theme, Typography, useTheme } from "@mui/material";
import { DownloadRowsReportDocument } from "../../graphql/types";
import type { DownloadRowsReportQuery, DownloadRowsReportQueryVariables } from "../../graphql/types";

import { format, getMonth, getYear } from "date-fns";
import { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import Switch from "../../icons/Switch.svg?react";
import { humanizeNumber } from "../../lib/Util";
import { DataLoader, ErrorContainer, LoadingContainer, SuccessContainer } from "../DataLoader";
import { GqlError } from "../common/GqlError";
import { CheckboxWithLabel } from "../ui/CheckboxWithLabel";
import { MultiSelect } from "../ui/MultiSelect";
import {
  ChartPatterns,
  CustomLegend,
  CustomTooltip,
  DatasetData,
  LoadingContent,
  buildDataset,
  getRangeDate,
} from "./ChartsCommon";

const useAllowedDataVisualizationColors = (theme: Theme) => {
  return [
    theme.palette.dataArctic.main,
    theme.palette.dataCream.main,
    theme.palette.dataLime.main,
    theme.palette.dataGinger.main,
    theme.palette.dataMagenta.main,
    theme.palette.dataMaroon.main,
    theme.palette.dataNavy.main,
    theme.palette.dataPeach.main,
    theme.palette.dataRed.main,
    theme.palette.dataSky.main,
  ];
};

const ALLOWED_TEXTURES_IDS = [
  "texturedArctic",
  "texturedCream",
  "texturedLime",
  "texturedGinger",
  "texturedMagenta",
  "texturedMaroon",
  "texturedNavy",
  "texturedPeach",
  "texturedRed",
  "texturedSky",
];

const MAX_PROJECTS_ALLOWED = 5;
type Series = {
  dataKey: string;
  label: string;
};

const ChartByProject = ({
  handleOnChange,
  useTextures,
  setUseTextures,
}: {
  handleOnChange: () => void;
  useTextures: boolean;
  setUseTextures: (value: boolean) => void;
}) => {
  const theme = useTheme();
  const ALLOWED_DATA_VISUALIZATION_COLORS = useAllowedDataVisualizationColors(theme);
  let dataset: Array<DatasetData> = [];
  let currentMonthRowCount = 0;
  const { data, loading, error, refetch } = useQuery<DownloadRowsReportQuery, DownloadRowsReportQueryVariables>(
    DownloadRowsReportDocument,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        dimensions: ["project"],
        filters: {
          monthsAgo: 12,
          topProjects: 5,
        },
      },
    }
  );

  const derivedProjects = useMemo(() => {
    if (!data) return [];
    const projects: string[] = [];
    data.downloadRowsReport?.rows?.forEach((row) => {
      if (row.project && row.project !== "Others") {
        projects.push(row.project);
      }
    });
    return projects;
  }, [data]);

  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [prevDerivedProjects, setPrevDerivedProjects] = useState(derivedProjects);
  if (prevDerivedProjects !== derivedProjects && selectedProjects.length === 0) {
    setPrevDerivedProjects(derivedProjects);
    setSelectedProjects(derivedProjects);
  }
  const getAllProjects = useQuery<DownloadRowsReportQuery, DownloadRowsReportQueryVariables>(
    DownloadRowsReportDocument,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        dimensions: ["project"],
        filters: {
          monthsAgo: 12,
        },
      },
    }
  );
  const allProjects: string[] = [];
  const filteredProjects: string[] = [];
  getAllProjects.data?.downloadRowsReport?.rows?.forEach((row) => {
    const datasetKey = row?.project;
    if (!datasetKey) return;
    if (!allProjects.includes(datasetKey)) {
      allProjects.push(datasetKey);
    }
  });
  if (data) {
    dataset = buildDataset(data.downloadRowsReport?.contracted);
    data.downloadRowsReport?.rows?.forEach((row) => {
      const datasetKey = row?.project;
      if (!datasetKey) return;
      if (!filteredProjects.includes(datasetKey)) {
        filteredProjects.push(datasetKey);
      }
      const existingData = dataset.find(
        (d) => d.month === format(new Date(row?.year, row?.month - 1), "MMM") && d.year === row?.year
      );
      if (existingData) {
        existingData[datasetKey] = row?.totalRowCount;
        if (row.year === getYear(new Date()) && row?.month === getMonth(new Date()) + 1) {
          currentMonthRowCount += row?.totalRowCount;
        }
      }
    });
  }
  if (dataset.length > 0 && data?.downloadRowsReport?.forecast) {
    dataset[dataset.length - 1].forecasted = data?.downloadRowsReport?.forecast - currentMonthRowCount;
  }

  const series: Series[] = filteredProjects.map((project) => ({
    dataKey: project,
    label: project,
  }));
  series.push({
    dataKey: "forecasted",
    label: "Forecasted",
  });
  const handleChangeProject = (value: string[]) => {
    if (value.length === 0) return;
    setSelectedProjects(value);
    refetch({
      dimensions: ["project"],
      filters: {
        monthsAgo: 12,
        projects: value,
      },
    });
  };
  const { startingDay, endingDay } = getRangeDate();
  const getColorsOrTextures = (index: number) => {
    if (useTextures) {
      // we don't want to show the forecasted texture in more than one bar
      // return the name of the key that is in index position
      return `url(#${ALLOWED_TEXTURES_IDS[index % ALLOWED_TEXTURES_IDS.length]})`;
    }
    return ALLOWED_DATA_VISUALIZATION_COLORS[index % ALLOWED_DATA_VISUALIZATION_COLORS.length];
  };
  return (
    <DataLoader loading={loading} error={error} success={Boolean(data)}>
      <LoadingContainer>
        <LoadingContent>
          <CircularProgress size="24px" />
        </LoadingContent>
      </LoadingContainer>
      <ErrorContainer>
        <GqlError error={error} />
      </ErrorContainer>
      <SuccessContainer>
        {data ? (
          <>
            <Stack direction="row" sx={{ flexWrap: "wrap", justifyContent: "space-between", gap: "10px" }}>
              <Stack sx={{ gap: "16px" }}>
                <Stack direction="row" sx={{ alignItems: "center", gap: "16px" }}>
                  <Typography variant="heading.four">Rows per project</Typography>
                  <Button variant="text" color="secondary" size="small" startIcon={<Switch />} onClick={handleOnChange}>
                    Change
                  </Button>
                </Stack>
                <Chip variant="outline" label={`${startingDay} - ${endingDay}`} />
              </Stack>
              <Stack direction="column" sx={{ gap: "12px", width: "300px" }}>
                <MultiSelect
                  options={allProjects}
                  handleChange={handleChangeProject}
                  value={selectedProjects}
                  maxItemsSelected={MAX_PROJECTS_ALLOWED}
                />
                <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
                  <CheckboxWithLabel
                    label="Use textures"
                    checked={useTextures}
                    onClick={() => setUseTextures(!useTextures)}
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack sx={{ mt: "66px", width: "100%", height: "480px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={dataset}
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 20,
                  }}
                  barSize={36}
                  // Workaround to separate bars https://github.com/recharts/recharts/issues/2244, but it does not work well width textures.
                >
                  {ChartPatterns}
                  <CartesianGrid strokeLinecap="round" vertical={false} />
                  <YAxis tickFormatter={humanizeNumber} />
                  <XAxis dataKey="label" />
                  <ChartTooltip formatter={(value) => humanizeNumber(value as number)} content={<CustomTooltip />} />
                  <Legend content={<CustomLegend />} />
                  {series.map((serie, index) => (
                    <Bar
                      key={serie.dataKey}
                      dataKey={serie.dataKey}
                      stackId="total"
                      name={serie.dataKey === "forecasted" ? "Forecasted" : serie.dataKey}
                      fill={serie.dataKey === "forecasted" ? "url(#forecasted)" : getColorsOrTextures(index)}
                    />
                  ))}
                  <Line
                    type="linear"
                    dataKey="contracted"
                    name="Contracted"
                    stroke={theme.palette.dataCerulean.main}
                    dot={false}
                    strokeWidth={4}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Stack>
          </>
        ) : null}
      </SuccessContainer>
    </DataLoader>
  );
};

export { ChartByProject };
